"use client";

import { useEffect, useRef, useState } from "react";
import { Icon } from "@iconify-icon/react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "motion/react";

import { PricingForm } from "./PricingForm";

import { toast } from "@/utils/customToast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { deletePricingConfiguration } from "@/features/calendar/api/Pricing";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/NonPortalPopover.tsx";
import {
  type ChildSubject,
  getCurrencySymbol,
  type PriceCurrency,
  type PricingConfiguration,
  type SubjectWithCurriculumLevels
} from "@/features/calendar/types/pricing";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";

interface SubjectPricingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subjects: SubjectWithCurriculumLevels[];
  existingPricings: PricingConfiguration[];
  onSuccess: () => void;
}

export const SubjectPricingDialog = ({
  open,
  onOpenChange,
  subjects: subjectsData,
  existingPricings: initialExistingPricings,
  onSuccess,
}: SubjectPricingDialogProps) => {
  const { t } = useTranslation(["common", "calendar"]);
  const [localPricings, setLocalPricings] =
    useState<PricingConfiguration[]>(initialExistingPricings);
  const [selectedSubjectId, setSelectedSubjectId] = useState<string | null>(null);
  const [openLevelPopover, setOpenLevelPopover] = useState<string | null>(null);
  const hasChangesRef = useRef(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPricingForDelete, setSelectedPricingForDelete] =
    useState<PricingConfiguration | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [expandedSubjects, setExpandedSubjects] = useState<Set<string>>(new Set());

  // Constants for limiting display
  const MAX_CHILDREN_PER_GROUP = 5;

  // Toggle expansion for a specific subject
  const toggleSubjectExpansion = (subjectId: string) => {
    setExpandedSubjects((prev) => {
      const newSet = new Set(prev);

      if (newSet.has(subjectId)) {
        newSet.delete(subjectId);
      } else {
        newSet.add(subjectId);
      }

      return newSet;
    });
  };

  // Count existing pricings for each subject
  const getExistingPricingsCount = (subjectId: string): number => {
    return localPricings.filter((p) => p.subject?.tutor_profile_subject_id === subjectId).length;
  };

  useEffect(() => {
    if (open) {
      setLocalPricings(initialExistingPricings);
      const firstSubject = subjectsData[0];

      if (firstSubject) {
        if (firstSubject.children && firstSubject.children.length > 0) {
          setSelectedSubjectId(firstSubject.children[0].id);
        } else {
          setSelectedSubjectId(firstSubject.id);
        }
      }
    }
  }, [initialExistingPricings, open, subjectsData]);

  const handleSinglePricingSave = (savedPricing: PricingConfiguration) => {
    hasChangesRef.current = true;
    setLocalPricings((prevList) => {
      const index = prevList.findIndex(
        (p) =>
          p.id === savedPricing.id ||
          (p.subject?.tutor_profile_subject_id === savedPricing.subject?.tutor_profile_subject_id &&
            p.curriculum_level?.id === savedPricing.curriculum_level?.id),
      );

      if (index !== -1) {
        const newList = [...prevList];

        newList[index] = savedPricing;

        return newList;
      }

      return [...prevList, savedPricing];
    });

    // Close the popover after successful save
    setOpenLevelPopover(null);
  };

  const handleDeleteConfirmation = (pricing: PricingConfiguration) => {
    setSelectedPricingForDelete(pricing);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedPricingForDelete) return;
    try {
      setIsDeleting(true);
      await deletePricingConfiguration(selectedPricingForDelete.id);
      toast.success(t("calendar:pricing.subjectPricingRemoved"));
      setLocalPricings((prev) => prev.filter((p) => p.id !== selectedPricingForDelete.id));
      hasChangesRef.current = true;
      setDeleteDialogOpen(false);
      setSelectedPricingForDelete(null);
    } catch (error) {
      console.error("Failed to delete subject pricing:", error);
      toast.error(t("common:errors.genericErrorTitle"), {
        description: t("common:errors.failedToRemovePricing"),
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCloseDialog = () => {
    if (hasChangesRef.current) {
      onSuccess();
    }
    hasChangesRef.current = false;
    setSelectedSubjectId(null);
    setDeleteDialogOpen(false);
    setSelectedPricingForDelete(null);
    setIsDeleting(false);
    onOpenChange(false);
  };

  const getExistingPricingForLevel = (tutorProfileSubjectId: string, curriculumLevelId: string) => {
    return (
      localPricings.find(
        (p) =>
          p.subject?.tutor_profile_subject_id === tutorProfileSubjectId &&
          p.curriculum_level?.id === curriculumLevelId,
      ) || null
    );
  };

  const findSelectedSubject = (): SubjectWithCurriculumLevels | ChildSubject | null => {
    if (!selectedSubjectId) return null;

    const parentSubject = subjectsData.find((s) => s.id === selectedSubjectId);

    if (parentSubject) return parentSubject;

    for (const parent of subjectsData) {
      if (parent.children) {
        const child = parent.children.find((c) => c.id === selectedSubjectId);

        if (child) return child;
      }
    }

    return null;
  };

  const selectedSubject = findSelectedSubject();
  const selectedSubjectName = selectedSubject
    ? "name" in selectedSubject
      ? selectedSubject.name
      : selectedSubject.subject?.name
    : "";

  return (
    <Dialog open={open} onOpenChange={handleCloseDialog}>
      <DialogContent
        aria-describedby={undefined}
        className="bg-foreground/80 flex h-[85dvh] max-h-[800px] w-full max-w-6xl flex-col overflow-hidden rounded-xl border-0 p-0 backdrop-blur-xl max-lg:h-dvh max-lg:max-h-dvh lg:h-[85vh]"
      >
        <div className="flex h-full flex-col overflow-hidden lg:flex-row">
          {/* Redesigned Modern Sidebar */}
          <div className="h-1/3 w-full flex-shrink-0 overflow-hidden border-b lg:h-full lg:w-80 lg:border-r lg:border-b-0">
            <div className="flex h-full flex-col">
              {/* Sidebar Header - Mobile only */}
              <div className="bg-background/95 flex flex-col items-start justify-between border-b p-4 backdrop-blur-sm lg:hidden">
                <h3 className="text-accent-foreground text-base font-semibold">
                  {t("calendar:pricing.subjects")}
                </h3>
                <span className="text-muted-foreground text-xs">
                  {t("calendar:pricing.selectSubjectTooltip")}
                </span>
              </div>

              {/* Subject List */}
              <ScrollArea className="h-full flex-1">
                <motion.div
                  animate={{ opacity: 1 }}
                  className="space-y-2 p-2 sm:space-y-3 sm:p-3"
                  initial={{ opacity: 1 }}
                  transition={{ duration: 0.2 }}
                >
                  {subjectsData.map((subject) => {
                    const isParentSelected = selectedSubjectId === subject.id;
                    const subjectPricingCount = getExistingPricingsCount(subject.id);
                    const hasChildren = subject.children && subject.children.length > 0;
                    const isSubjectExpanded = expandedSubjects.has(subject.id);
                    const hasMoreChildren =
                      hasChildren &&
                      subject.children &&
                      subject.children.length > MAX_CHILDREN_PER_GROUP;

                    return (
                      <div key={subject.id} className="space-y-1">
                        {/* Parent Subject */}
                        <button
                          className={cn(
                            "group flex w-full items-center gap-2 rounded-lg px-2.5 py-2 transition-all duration-200 sm:gap-3 sm:px-3",
                            isParentSelected
                              ? "bg-side-accent text-accent-foreground shadow-sm"
                              : "hover:bg-muted/50 text-muted-foreground hover:text-accent-foreground",
                          )}
                          onClick={() => {
                            setSelectedSubjectId(subject.id);
                            setOpenLevelPopover(null);
                          }}
                        >
                          <div
                            className={cn(
                              "flex h-7 w-7 items-center justify-center rounded-md transition-all duration-200 sm:h-8 sm:w-8 sm:rounded-lg",
                              isParentSelected ? "bg-muted" : "bg-muted/50 group-hover:bg-muted",
                            )}
                          >
                            <Icon
                              className={cn(
                                isParentSelected
                                  ? "text-accent-foreground"
                                  : "text-muted-foreground",
                              )}
                              height="14"
                              icon={subject.icon || "mdi:book-education"}
                              width="14"
                            />
                          </div>
                          <span className="flex-1 text-left text-xs font-medium sm:text-sm">
                            {subject.name}
                          </span>
                          {subjectPricingCount > 0 && (
                            <div
                              className={cn(
                                "rounded-full px-1.5 py-0.5 text-[10px] font-medium transition-colors duration-200 sm:px-2 sm:text-xs",
                                isParentSelected
                                  ? "bg-muted text-accent-foreground"
                                  : "bg-muted/50 text-muted-foreground group-hover:bg-muted group-hover:text-accent-foreground",
                              )}
                            >
                              {subjectPricingCount}
                            </div>
                          )}
                        </button>

                        {/* Children - Always Visible */}
                        {hasChildren && subject.children && (
                          <div className="border-muted/30 ml-5 space-y-1 border-l-2 pl-2.5 sm:ml-6 sm:pl-3">
                            {subject.children
                              .slice(0, isSubjectExpanded ? undefined : MAX_CHILDREN_PER_GROUP)
                              .map((child) => {
                                const childPricingCount = getExistingPricingsCount(child.id);
                                const isChildSelected = selectedSubjectId === child.id;

                                return (
                                  <button
                                    key={child.id}
                                    className={cn(
                                      "group flex w-full items-center gap-2 rounded-lg px-2 py-1.5 transition-all duration-200 sm:gap-2.5 sm:px-2.5 sm:py-2",
                                      isChildSelected
                                        ? "bg-side-accent text-accent-foreground shadow-sm"
                                        : "hover:bg-muted/50 text-muted-foreground hover:text-accent-foreground",
                                    )}
                                    onClick={() => {
                                      setSelectedSubjectId(child.id);
                                      setOpenLevelPopover(null);
                                    }}
                                  >
                                    <div
                                      className={cn(
                                        "flex h-5 w-5 items-center justify-center rounded-md transition-all duration-200 sm:h-6 sm:w-6",
                                        isChildSelected
                                          ? "bg-muted"
                                          : "bg-muted/30 group-hover:bg-muted/50",
                                      )}
                                    >
                                      <Icon
                                        className={cn(
                                          isChildSelected
                                            ? "text-accent-foreground"
                                            : "text-muted-foreground",
                                        )}
                                        height="11"
                                        icon={child.subject.icon || "mdi:book-variant"}
                                        width="11"
                                      />
                                    </div>
                                    <span className="flex-1 text-left text-[11px] font-medium sm:text-xs">
                                      {child.subject.name}
                                    </span>
                                    {childPricingCount > 0 && (
                                      <div
                                        className={cn(
                                          "rounded-full px-1 py-0.5 text-[10px] font-medium transition-colors duration-200 sm:text-xs",
                                          isChildSelected
                                            ? "bg-muted text-accent-foreground"
                                            : "bg-muted/30 text-muted-foreground group-hover:bg-muted/50 group-hover:text-accent-foreground",
                                        )}
                                      >
                                        {childPricingCount}
                                      </div>
                                    )}
                                  </button>
                                );
                              })}

                            {/* Per-subject expand/collapse button */}
                            {hasMoreChildren && (
                              <button
                                className="text-muted-foreground/70 hover:bg-muted/20 hover:text-muted-foreground flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-xs transition-all duration-200"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleSubjectExpansion(subject.id);
                                }}
                              >
                                <Icon
                                  className="transition-transform duration-200"
                                  height="11"
                                  icon={isSubjectExpanded ? "mdi:chevron-up" : "mdi:chevron-down"}
                                  width="11"
                                />
                                <span>
                                  {isSubjectExpanded
                                    ? t("common:showLess")
                                    : `+${subject.children.length - MAX_CHILDREN_PER_GROUP} ${t("common:more")}`}
                                </span>
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </motion.div>
              </ScrollArea>
            </div>
          </div>

          {/* Main Content */}
          <div className="bg-background h-2/3 flex-1 overflow-hidden lg:h-full">
            <div className="flex h-full flex-col overflow-hidden p-4 lg:p-6">
              {selectedSubject ? (
                <div className="flex h-full flex-col">
                  {/* Content Header with Title - No animation */}
                  <div className="mb-3 flex-shrink-0 sm:mb-4 lg:mb-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-accent-foreground text-base font-semibold sm:text-lg lg:text-xl">
                          {t("calendar:pricing.configureSubjectPricing")}
                        </h2>
                        <p className="text-muted-foreground mt-1 text-[11px] sm:text-xs lg:text-sm">
                          {selectedSubjectName}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Compact Curriculum Levels Grid */}
                  <AnimatePresence mode="wait">
                    <ScrollArea key={selectedSubject.id} className="flex-1">
                      <motion.div
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-3 pb-4"
                        exit={{ opacity: 0, y: -20 }}
                        initial={{ opacity: 0, y: 20 }}
                        transition={{ duration: 0.3 }}
                      >
                        {/* Apply to All Levels Card */}
                        {selectedSubject.curriculum_levels.length > 1 && (
                          <Popover
                            modal={true}
                            open={openLevelPopover === `${selectedSubject.id}-all`}
                            onOpenChange={(isOpen) =>
                              setOpenLevelPopover(isOpen ? `${selectedSubject.id}-all` : null)
                            }
                          >
                            <PopoverTrigger asChild>
                              <div
                                className={cn(
                                  "group relative cursor-pointer overflow-hidden rounded-2xl border-2 border-dashed p-5 transition-all duration-300",
                                  openLevelPopover === `${selectedSubject.id}-all`
                                    ? "border-accent from-accent/20 via-accent/10 shadow-accent/20 bg-gradient-to-br to-transparent shadow-lg"
                                    : "border-muted/50 from-muted/20 via-muted/10 hover:border-accent/50 hover:shadow-accent/10 bg-gradient-to-br to-transparent hover:shadow-md",
                                )}
                                role="button"
                                tabIndex={0}
                                onClick={() => setOpenLevelPopover(`${selectedSubject.id}-all`)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    setOpenLevelPopover(`${selectedSubject.id}-all`);
                                  }
                                }}
                              >
                                <div className="from-accent/5 absolute inset-0 bg-gradient-to-br to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                                <div className="relative flex items-center justify-between">
                                  <div className="flex items-center gap-4">
                                    <div className="from-accent/20 to-accent/10 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br shadow-inner">
                                      <Icon
                                        className="text-accent-foreground"
                                        height="24"
                                        icon="mdi:layers-triple"
                                        width="24"
                                      />
                                    </div>
                                    <div className="text-left">
                                      <h4 className="text-accent-foreground text-base font-semibold">
                                        {t("calendar:pricing.applyToAllLevels")}
                                      </h4>
                                      <p className="text-muted-foreground/80 text-sm">
                                        {t("calendar:pricing.setSamePriceForAllLevels")}
                                      </p>
                                    </div>
                                  </div>
                                  <Icon
                                    className="text-muted-foreground transition-transform duration-200 group-hover:translate-x-1"
                                    height="20"
                                    icon="mdi:chevron-right"
                                    width="20"
                                  />
                                </div>
                              </div>
                            </PopoverTrigger>
                            <PopoverContent className="z-[2000]! w-96">
                              <PricingForm
                                allPricingsForSubjectForBatch={localPricings.filter(
                                  (p) => p.subject?.tutor_profile_subject_id === selectedSubject.id,
                                )}
                                applyToLevels={selectedSubject.curriculum_levels}
                                subjectId={
                                  "subject" in selectedSubject && selectedSubject.subject
                                    ? selectedSubject.subject.id
                                    : selectedSubject.id
                                }
                                subjectsData={subjectsData}
                                tutorProfileSubjectId={selectedSubject.id}
                                onBatchOperationComplete={(results) => {
                                  hasChangesRef.current = true;
                                  setLocalPricings((prevList) => {
                                    const updatedList = [...prevList];

                                    results.forEach((newPricing) => {
                                      const index = updatedList.findIndex(
                                        (p) =>
                                          p.subject?.tutor_profile_subject_id ===
                                            newPricing.subject?.tutor_profile_subject_id &&
                                          p.curriculum_level?.id ===
                                            newPricing.curriculum_level?.id,
                                      );

                                      if (index !== -1) {
                                        updatedList[index] = newPricing;
                                      } else {
                                        updatedList.push(newPricing);
                                      }
                                    });

                                    return updatedList;
                                  });
                                  setOpenLevelPopover(null);
                                }}
                                onCancel={() => setOpenLevelPopover(null)}
                              />
                            </PopoverContent>
                          </Popover>
                        )}

                        {/* Individual Curriculum Level Cards */}
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3">
                          {selectedSubject.curriculum_levels.map((level) => {
                            const existingPricing = getExistingPricingForLevel(
                              selectedSubject.id,
                              level.id,
                            );
                            const popoverKey = `${selectedSubject.id}-${level.id}`;
                            const subjectIdForForm =
                              "subject" in selectedSubject && selectedSubject.subject
                                ? selectedSubject.subject.id
                                : selectedSubject.id;

                            return (
                              <Popover
                                key={level.id}
                                modal={true}
                                open={openLevelPopover === popoverKey}
                                onOpenChange={(isOpen) =>
                                  setOpenLevelPopover(isOpen ? popoverKey : null)
                                }
                              >
                                <PopoverTrigger asChild>
                                  <div
                                    className={cn(
                                      "group relative cursor-pointer overflow-hidden rounded-xl border backdrop-blur-sm transition-all duration-300 sm:rounded-2xl",
                                      openLevelPopover === popoverKey
                                        ? "border-accent from-accent/15 via-side-accent/80 to-accent/10 shadow-accent/20 bg-gradient-to-br shadow-lg"
                                        : existingPricing
                                          ? "border-accent/30 from-side-accent/50 via-muted/30 to-side-accent/20 hover:border-accent/50 hover:shadow-accent/10 bg-gradient-to-br hover:shadow-md"
                                          : "border-muted/40 from-muted/20 via-background/50 to-muted/10 hover:border-accent/30 bg-gradient-to-br hover:shadow-md",
                                    )}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => setOpenLevelPopover(popoverKey)}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter" || e.key === " ") {
                                        setOpenLevelPopover(popoverKey);
                                      }
                                    }}
                                  >
                                    {/* Gradient overlay for hover effect */}
                                    <div className="from-accent/5 absolute inset-0 bg-gradient-to-br to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

                                    {/* Card content */}
                                    <div className="relative p-4 sm:p-5">
                                      <div className="mb-2 flex items-start justify-between sm:mb-3">
                                        <div className="flex-1">
                                          <h4 className="text-accent-foreground text-sm font-semibold sm:text-base">
                                            {level.name}
                                          </h4>
                                          {existingPricing && (
                                            <p className="text-muted-foreground mt-0.5 text-[11px] sm:mt-1 sm:text-xs">
                                              {t("calendar:pricing.configured")}
                                            </p>
                                          )}
                                        </div>
                                        {!existingPricing && (
                                          <div className="bg-muted/50 flex h-7 w-7 items-center justify-center rounded-md sm:h-8 sm:w-8 sm:rounded-lg">
                                            <Icon
                                              className="text-muted-foreground"
                                              height="14"
                                              icon="mdi:plus"
                                              width="14"
                                            />
                                          </div>
                                        )}
                                      </div>

                                      {existingPricing ? (
                                        <div className="flex items-center justify-between">
                                          <div className="flex items-center gap-2 sm:gap-3">
                                            <div className="from-accent/20 to-accent/10 flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br sm:h-10 sm:w-10 sm:rounded-xl">
                                              <Icon
                                                className="text-accent-foreground"
                                                height="18"
                                                icon="mdi:cash"
                                                width="18"
                                              />
                                            </div>
                                            <span className="text-accent-foreground text-xl font-bold sm:text-2xl">
                                              {getCurrencySymbol(
                                                existingPricing.currency as PriceCurrency,
                                              )}
                                              {existingPricing.amount.toFixed(2)}
                                            </span>
                                          </div>
                                          <Button
                                            className="bg-destructive/10 text-destructive hover:bg-destructive/20 hover:text-destructive h-7 w-7 rounded-md sm:h-8 sm:w-8 sm:rounded-lg"
                                            size="icon"
                                            variant="ghost"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDeleteConfirmation(existingPricing);
                                            }}
                                          >
                                            <Icon
                                              height="14"
                                              icon="mdi:trash-can-outline"
                                              width="14"
                                            />
                                            <span className="sr-only">{t("common:delete")}</span>
                                          </Button>
                                        </div>
                                      ) : (
                                        <div className="text-muted-foreground flex items-center gap-1.5 sm:gap-2">
                                          <Icon
                                            height="14"
                                            icon="mdi:currency-usd-off"
                                            width="14"
                                          />
                                          <span className="text-xs sm:text-sm">
                                            {t("calendar:pricing.notSet")}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </PopoverTrigger>
                                <PopoverContent
                                  align="center"
                                  className="z-[2000] w-[calc(100vw-2rem)] sm:w-96"
                                  side="top"
                                >
                                  <PricingForm
                                    curriculumLevelId={level.id}
                                    existingPricing={existingPricing}
                                    subjectId={subjectIdForForm}
                                    subjectsData={subjectsData}
                                    tutorProfileSubjectId={selectedSubject.id}
                                    onCancel={() => setOpenLevelPopover(null)}
                                    onSave={handleSinglePricingSave}
                                  />
                                </PopoverContent>
                              </Popover>
                            );
                          })}
                        </div>
                      </motion.div>
                    </ScrollArea>
                  </AnimatePresence>
                </div>
              ) : (
                <motion.div
                  key="no-subject-selected"
                  animate={{ opacity: 1, y: 0 }}
                  className="flex h-full items-center justify-center p-4"
                  exit={{ opacity: 0, y: -20 }}
                  initial={{ opacity: 0, y: 20 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="text-center">
                    <div className="bg-muted/30 mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-lg lg:mb-4 lg:h-14 lg:w-14">
                      <Icon
                        className="text-muted-foreground"
                        height="18"
                        icon="mdi:book-education-outline"
                        width="18"
                      />
                    </div>
                    <h3 className="text-accent-foreground mb-1 text-sm font-medium sm:text-base lg:mb-2 lg:text-lg">
                      {t("calendar:pricing.configureSubjectPricing")}
                    </h3>
                    <p className="text-muted-foreground text-[11px] sm:text-xs lg:text-sm">
                      {t("calendar:pricing.selectSubjectTooltip")}
                    </p>
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("calendar:pricing.removeSubjectPricing")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("calendar:pricing.removeSubjectPricingConfirmation", {
                subject: selectedPricingForDelete?.subject?.name,
                level: selectedPricingForDelete?.curriculum_level?.name || "",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>{t("common:cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isDeleting}
              onClick={handleDelete}
            >
              {isDeleting ? (
                <>
                  <Icon className="mr-2 animate-spin" height="16" icon="mdi:loading" width="16" />
                  {t("common:deleting")}
                </>
              ) : (
                t("calendar:pricing.removePricing")
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
};
